import React, { useCallback, useEffect, useMemo, useState } from "react"
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Divider,
  Drawer,
  Empty,
  Modal,
  Progress,
  Row,
  Skeleton,
  Space,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd"
import { CheckCircleTwoTone, StopOutlined } from "@ant-design/icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faBan,
  faBoxArchive,
  faCircleCheck,
  faEnvelope,
  faEnvelopeOpen,
  faImage,
  faRotateLeft,
} from "@fortawesome/pro-regular-svg-icons"
import { IconDefinition } from "@fortawesome/fontawesome-svg-core"
import { useHistory } from "react-router-dom"
import { useStoreState, StateMapper } from "easy-peasy"
import { AppDataStore } from "../appData/types"
import { gen, swimminglyApi } from "./utils"
import TeamLogosManager from "./TeamLogosManager"
import GuardianMeetLifecyclePreview from "./GuardianMeetLifecyclePreview"
import InPersonMeetCreatedPreview from "./InPersonMeetCreatedPreview"
import VirtualMeetInvitationPreview from "./VirtualMeetInvitationPreview"
import VirtualMeetResultsPublishedPreview from "./VirtualMeetResultsPublishedPreview"
import WebsitesTemplatesTeaser from "./websites/WebsitesTemplatesTeaser"
import { AppUpdatedTagList } from "./AppUpdatedTag"

const { Paragraph, Text } = Typography

const ACTIVITY_FEED_REFETCH_EVENT = "swimmingly:activity-feed:refetch"

interface ActivityFeedDrawerProps {
  visible: boolean
  setVisible: (value: boolean) => void
}

interface ActivityFeedSummary {
  unreadCount: number
}

interface ActivityFeedAction {
  kind?: string
  route?: string
  tabKey?: string
  paymentId?: number | null
  clubId?: number | null
}

interface ActivityFeedActionButtonConfig {
  label: string | ((item: ActivityFeedItem) => string)
  icon?: IconDefinition
}

interface ResolvedActivityFeedActionButtonConfig {
  label: string
  icon?: IconDefinition
}

interface ActivityFeedInlineSlotDefinition {
  actionButton: ActivityFeedActionButtonConfig
  sectionLabel?: string | ((item: ActivityFeedItem) => string)
  renderInlineComponent?: (item: ActivityFeedItem) => JSX.Element | null
  renderDesktopInlineComponent?: (item: ActivityFeedItem) => JSX.Element | null
}

interface ActivityFeedItem {
  itemId: string
  typeKey: string
  title: string
  body: string | null
  clubId?: number | null
  completionReason?: string | null
  completionTitle?: string | null
  completionBody?: string | null
  status: string
  categoryKey: string | null
  customerJourneyStep: string | null
  recipientIntent: string | null
  priority: string | null
  isUnread: boolean
  isDismissed: boolean
  dismissedAt?: string | null
  archivedAt?: string | null
  triggeredAt: string | null
  completedAt?: string | null
  completedByName?: string | null
  completedByEffectiveUserId?: number | null
  completedBySessionUserId?: number | null
  context?: Record<string, any>
  capabilities: {
    canRead: boolean
    canArchive: boolean
    canDismiss: boolean
    canTakeAction: boolean
  }
  primaryAction?: ActivityFeedAction | null
}

function buildGuardianParentMeetScheduleRoute(item: ActivityFeedItem): string | null {
  if (item.typeKey !== "guardian_meet_lifecycle") {
    return null
  }

  const lifecycleState = String(item.context?.lifecycleState || "")
  if (lifecycleState !== "created" && lifecycleState !== "heat_sheet_merged") {
    return null
  }

  const clubId = Number(item.clubId || item.context?.clubId || 0)
  const seasonId = Number(item.context?.seasonId || 0)
  const meetId = Number(item.context?.meetId || 0)

  const params = new URLSearchParams()
  if (clubId > 0) {
    params.set("clubId", String(clubId))
  }
  if (seasonId > 0) {
    params.set("seasonId", String(seasonId))
  }
  if (meetId > 0) {
    params.set("meetId", String(meetId))
  }

  const query = params.toString()
  return query ? `/app/parentMeets?${query}` : "/app/parentMeets"
}

interface MeetDeclarationsSwimmer {
  swimmerId: number
  firstName: string | null
  lastName: string | null
  status: "attending" | "notAttending" | "pending"
  lastDeclarationActorName?: string | null
}

const INLINE_AFN_SLOT_DEFINITIONS: Record<string, ActivityFeedInlineSlotDefinition> = {
  admin_approval_request: {
    actionButton: {
      label: "Review Request",
    },
  },
  version_update: {
    actionButton: {
      label: "I've Updated My Devices",
    },
  },
  upload_team_logo_missing: {
    actionButton: {
      label: "Go to my team's logos",
      icon: faImage,
    },
    renderDesktopInlineComponent: (item: ActivityFeedItem) => {
      const clubId = Number(item.clubId || item.context?.clubId || 0)
      if (!clubId) {
        return (
          <Alert
            showIcon
            type="info"
            title="Open your Team Logos page to complete this action."
          />
        )
      }

      return <TeamLogosManager clubId={clubId} />
    },
  },
  activate_club_website_missing: {
    sectionLabel: "Website Preview",
    actionButton: {
      label: "Go to Websites",
    },
    renderInlineComponent: () => {
      return <WebsitesTemplatesTeaser variant="embedded" forceMobileView={false} />
    },
  },
  in_person_meet_results_published: {
    sectionLabel: "Swim Meet Preview",
    actionButton: {
      label: "Go to Swim Meet",
    },
    renderInlineComponent: (item: ActivityFeedItem) => {
      return <InPersonMeetCreatedPreview item={item} />
    },
  },
  in_person_meet_created: {
    sectionLabel: "Swim Meet Preview",
    actionButton: {
      label: "Go to Swim Meet",
    },
    renderInlineComponent: (item: ActivityFeedItem) => {
      return <InPersonMeetCreatedPreview item={item} />
    },
  },
  virtual_meet_results_published: {
    sectionLabel: "Virtual Meet Preview",
    actionButton: {
      label: "Go to Swim Meet",
    },
    renderInlineComponent: (item: ActivityFeedItem) => {
      return <VirtualMeetResultsPublishedPreview item={item} />
    },
  },
  virtual_meet_invitation: {
    sectionLabel: "Virtual Meet Invitation",
    actionButton: {
      label: "View Virtual Meet Invitation",
    },
    renderInlineComponent: (item: ActivityFeedItem) => {
      return <VirtualMeetInvitationPreview item={item} />
    },
  },
  guardian_meet_lifecycle: {
    sectionLabel: (item: ActivityFeedItem) => {
      const lifecycleState = String(item.context?.lifecycleState || "")

      if (lifecycleState === "declarations_open" || lifecycleState === "declarations_closed") {
        return "Your Swimmers"
      }

      if (
        lifecycleState === "created" &&
        (Number(item.context?.virtualMeetInvitationId || 0) > 0 ||
          Number(item.context?.virtualMeetId || 0) > 0)
      ) {
        return "Virtual Meet Preview"
      }

      return "Swim Meet Preview"
    },
    actionButton: {
      label: (item) => {
        const lifecycleState = String(item.context?.lifecycleState || "")

        if (lifecycleState === "declarations_open" || lifecycleState === "declarations_closed") {
          return "Go to Meet Declarations"
        }

        if (lifecycleState === "results_published") {
          return "View Swim Meet Results"
        }

        return "Go to Swim Meet"
      },
    },
    renderInlineComponent: (item: ActivityFeedItem) => {
      return <GuardianMeetLifecyclePreview item={item} />
    },
  },
  meet_declarations_open: {
    actionButton: {
      label: "Go to Meet Declarations",
    },
  },
}

function titleize(value: string | null | undefined) {
  if (!value) return "All"
  return String(value)
    .replace(/([A-Z])/g, " $1")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (match) => match.toUpperCase())
}

function formatTimestamp(value: string | null) {
  if (!value) return ""
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return ""

  const dateLabel = parsed.toLocaleDateString([], {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  })
  const timeLabel = parsed.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  })

  return `${dateLabel}, ${timeLabel}`
}

function buildQueryString(params: Record<string, string | number | null | undefined>) {
  const query = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== "") {
      query.set(key, String(value))
    }
  })
  return query.toString()
}

function getCompletedByLabel(item: ActivityFeedItem) {
  if (item.completedByName) return item.completedByName
  if (item.completedByEffectiveUserId) return `User #${item.completedByEffectiveUserId}`
  if (item.completedBySessionUserId) return `User #${item.completedBySessionUserId}`
  return "A club admin"
}

function getCardTitle(item: ActivityFeedItem, variant: "default" | "completed" | "ignored" | "archived") {
  if (variant === "completed") {
    return item.completionTitle || item.title
  }
  return item.title
}

function getMeetDeclarationsDrawerSubtitle(item: ActivityFeedItem) {
  const meetName = String(item.context?.meetName || "").trim()
  const clubName = String(item.context?.clubNameLong || item.context?.clubNameShort || "").trim()
  const parts = [meetName, clubName].filter(Boolean)

  if (!parts.length) {
    return null
  }

  return parts.join(" • ")
}

function getAdminMeetRelatedDrawerSubtitle(item: ActivityFeedItem) {
  const supportedTypeKeys = new Set([
    "in_person_meet_created",
    "virtual_meet_invitation",
    "meet_declarations_open",
    "in_person_meet_results_published",
    "virtual_meet_results_published",
    "meet_removed",
    "virtual_meet_club_removed",
  ])

  if (!supportedTypeKeys.has(item.typeKey)) {
    return null
  }

  const meetName = String(
    item.context?.virtualMeetName || item.context?.meetName || "",
  ).trim()
  const clubName = String(item.context?.clubNameLong || item.context?.clubNameShort || "").trim()
  const parts = [meetName, clubName].filter(Boolean)

  if (!parts.length) {
    return null
  }

  return parts.join(" • ")
}

function getGuardianLifecycleDrawerSubtitle(item: ActivityFeedItem) {
  const subtitle = String(item.body || "").trim()
  return subtitle || null
}

function getCardBody(item: ActivityFeedItem, variant: "default" | "completed" | "ignored" | "archived") {
  if (item.typeKey === "guardian_meet_lifecycle") {
    return null
  }

  if (variant === "completed") {
    return item.completionBody || item.body
  }
  return item.body
}

function getVirtualMeetInvitationCompletionOutcome(item: ActivityFeedItem) {
  if (item.typeKey !== "virtual_meet_invitation") return null

  if (item.completionReason === "accepted" || item.completionReason === "declined") {
    return item.completionReason
  }

  const completionTitle = String(item.completionTitle || "").toLowerCase()
  if (completionTitle.includes("accepted")) return "accepted"
  if (completionTitle.includes("declined")) return "declined"

  return null
}

function renderCompletionOutcomeTag(item: ActivityFeedItem) {
  const outcome = getVirtualMeetInvitationCompletionOutcome(item)
  if (!outcome) return null

  return outcome === "declined" ? <Tag color="error">Declined</Tag> : <Tag color="success">Accepted</Tag>
}

function getCompletedByText(item: ActivityFeedItem) {
  const actorLabel = getCompletedByLabel(item)
  const outcome = getVirtualMeetInvitationCompletionOutcome(item)

  if (outcome === "accepted") return `Accepted by ${actorLabel}`
  if (outcome === "declined") return `Declined by ${actorLabel}`
  return `Completed by ${actorLabel}`
}

function isCloseOnlyDetailType(item: ActivityFeedItem | null) {
  return item?.typeKey === "virtual_meet_club_removed"
}

function renderJourneyStepTag(item: ActivityFeedItem, variant: "default" | "completed" | "ignored" | "archived") {
  if (!item.customerJourneyStep) return null

  const icon =
    variant === "completed"
      ? faCircleCheck
      : variant === "ignored"
        ? faBan
        : null

  return (
    <Tag color="cyan">
      <Space size={4}>
        {icon ? <FontAwesomeIcon icon={icon} /> : null}
        <span>{titleize(item.customerJourneyStep)}</span>
      </Space>
    </Tag>
  )
}

function renderCardTitleContent(
  item: ActivityFeedItem,
  variant: "default" | "completed" | "ignored" | "archived",
  isUnreadActivityCard: boolean,
) {
  const cardTitle = getCardTitle(item, variant)
  const adminMeetRelatedSubtitle = getAdminMeetRelatedDrawerSubtitle(item)
  const guardianLifecycleSubtitle =
    item.typeKey === "guardian_meet_lifecycle" ? getGuardianLifecycleDrawerSubtitle(item) : null

  if (item.typeKey === "meet_declarations_open") {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          columnGap: 6,
          rowGap: 1,
          minWidth: 0,
          flexWrap: "wrap",
          lineHeight: 1.1,
        }}
      >
        <span style={{ fontWeight: isUnreadActivityCard ? 700 : 600, color: "#0f172a" }}>
          Meet Declarations Open
        </span>
        {adminMeetRelatedSubtitle ? (
          <span
            style={{
              fontSize: 11,
              color: "#667085",
              fontWeight: 500,
              minWidth: 0,
              lineHeight: 1.05,
            }}
          >
            {adminMeetRelatedSubtitle}
          </span>
        ) : null}
      </div>
    )
  }

  if (item.typeKey === "version_update") {
    const appsUpdated = item.context?.appsUpdated as string[] | undefined
    const appUpdated = item.context?.appUpdated as string | undefined
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          rowGap: 4,
          minWidth: 0,
          lineHeight: 1.1,
        }}
      >
        <span style={{ fontWeight: isUnreadActivityCard ? 700 : 600, color: "#0f172a" }}>
          {cardTitle}
        </span>
        <AppUpdatedTagList appsUpdated={appsUpdated} appUpdated={appUpdated} />
      </div>
    )
  }

  if (adminMeetRelatedSubtitle) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          rowGap: 2,
          minWidth: 0,
          lineHeight: 1.1,
        }}
      >
        <span style={{ fontWeight: isUnreadActivityCard ? 700 : 600, color: "#0f172a" }}>
          {cardTitle}
        </span>
        <span
          style={{
            fontSize: 11,
            color: "#667085",
            fontWeight: 500,
            minWidth: 0,
            lineHeight: 1.05,
          }}
        >
          {adminMeetRelatedSubtitle}
        </span>
      </div>
    )
  }

  if (item.typeKey === "guardian_meet_lifecycle") {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          rowGap: 2,
          minWidth: 0,
          lineHeight: 1.1,
        }}
      >
        <span style={{ fontWeight: isUnreadActivityCard ? 700 : 600, color: "#0f172a" }}>
          {cardTitle}
        </span>
        {guardianLifecycleSubtitle ? (
          <span
            style={{
              fontSize: 11,
              color: "#667085",
              fontWeight: 500,
              minWidth: 0,
              lineHeight: 1.05,
            }}
          >
            {guardianLifecycleSubtitle}
          </span>
        ) : null}
      </div>
    )
  }

  return <span style={{ fontWeight: isUnreadActivityCard ? 600 : 500 }}>{cardTitle}</span>
}

function getDetailModalTitle(item: ActivityFeedItem, variant: "default" | "completed" | "ignored" | "archived") {
  if (variant === "completed") {
    return item.completionTitle || item.title
  }

  if (item.typeKey === "meet_declarations_open") {
    return "Meet Declarations Open"
  }

  return item.title
}

function renderModalSectionLabel(label: string) {
  return (
    <Text
      type="secondary"
      style={{
        display: "block",
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        marginBottom: 8,
      }}
    >
      {label}
    </Text>
  )
}

function renderInPersonResultsPublishedDetailNote(item: ActivityFeedItem) {
  if (item.typeKey !== "in_person_meet_results_published") {
    return null
  }

  return (
    <Alert
      showIcon
      type="success"
      title="Results are out and families are already in the loop."
      description="Every parent and guardian in your club with swimmers in the meet received a personalized email summary with a direct link to view results. Nice work getting the latest update out to your families."
    />
  )
}

function renderVirtualMeetResultsPublishedDetailNote(item: ActivityFeedItem) {
  if (item.typeKey !== "virtual_meet_results_published") {
    return null
  }

  return (
    <Alert
      showIcon
      type="success"
      title="Results are out and families are already in the loop."
      description="Every parent and guardian in your club with swimmers in the meet received a personalized email summary with a direct link to view results. Nice work getting the latest update out to your families."
    />
  )
}

function renderActivityFeedDetailTitleMeta(
  item: ActivityFeedItem,
  variant: "default" | "completed" | "ignored" | "archived",
  isCompactViewport: boolean,
) {
  const isUnreadActivityCard = variant === "default" && item.isUnread
  const cardTimestamp =
    variant === "ignored" && item.dismissedAt
      ? `Ignored ${formatTimestamp(item.dismissedAt)}`
      : variant === "completed" && item.completedAt
        ? `Completed ${formatTimestamp(item.completedAt)}`
        : variant === "archived" && item.archivedAt
          ? `Archived ${formatTimestamp(item.archivedAt)}`
          : formatTimestamp(item.triggeredAt)

  return (
    <div
      style={{
        display: "flex",
        flexDirection: isCompactViewport ? "column" : "row",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: isCompactViewport ? 8 : 16,
        width: "100%",
        maxWidth: isCompactViewport ? "100%" : "calc(100% - 28px)",
        boxSizing: "border-box",
      }}
    >
      <div style={{ minWidth: 0, flex: 1 }}>
        {getAdminMeetRelatedDrawerSubtitle(item) || item.typeKey === "guardian_meet_lifecycle" || item.typeKey === "version_update" ? (
          renderCardTitleContent(item, variant, isUnreadActivityCard)
        ) : (
          <span style={{ fontWeight: isUnreadActivityCard ? 600 : 500 }}>
            {getDetailModalTitle(item, variant)}
          </span>
        )}
      </div>
      <Space
        orientation="vertical"
        size={2}
        align={isCompactViewport ? "start" : "end"}
        style={{ minWidth: isCompactViewport ? undefined : 118 }}
      >
        <div style={{ minHeight: 22 }}>{renderJourneyStepTag(item, variant)}</div>
        <Text type="secondary" style={{ fontSize: 11, lineHeight: 1.2, opacity: 0.72 }}>
          {isUnreadActivityCard ? (
            <span
              style={{
                display: "inline-block",
                width: 5,
                height: 5,
                borderRadius: "50%",
                background: "#1677ff",
                marginRight: 5,
                verticalAlign: "middle",
              }}
            />
          ) : null}
          {cardTimestamp}
        </Text>
      </Space>
    </div>
  )
}

export default function ActivityFeedDrawer({
  visible,
  setVisible,
}: ActivityFeedDrawerProps): JSX.Element {
  const history = useHistory()
  const user = useStoreState((state: StateMapper<AppDataStore>) => state.user)
  const [summary, setSummary] = useState<ActivityFeedSummary | null>(null)
  const [items, setItems] = useState<ActivityFeedItem[]>([])
  const [completedItems, setCompletedItems] = useState<ActivityFeedItem[]>([])
  const [dismissedItems, setDismissedItems] = useState<ActivityFeedItem[]>([])
  const [archivedItems, setArchivedItems] = useState<ActivityFeedItem[]>([])
  const [activeTab, setActiveTab] = useState<"main" | "completed" | "dismissed" | "archived">("main")
  const [loadedTabs, setLoadedTabs] = useState<Set<string>>(new Set(["main"]))
  const [loading, setLoading] = useState(false)
  const [tabLoading, setTabLoading] = useState(false)
  const [mutatingIds, setMutatingIds] = useState<string[]>([])
  const [ignoredTabNotice, setIgnoredTabNotice] = useState<string | null>(null)
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null)
  const [detailVisible, setDetailVisible] = useState(false)
  const [detailItem, setDetailItem] = useState<ActivityFeedItem | null>(null)
  const [isCompactViewport, setIsCompactViewport] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false,
  )

  const selectedItem = useMemo(
    () => detailItem || items.find((item) => item.itemId === selectedItemId) || null,
    [detailItem, items, selectedItemId],
  )

  const selectedInlineSlotDefinition = useMemo(
    () => (selectedItem ? INLINE_AFN_SLOT_DEFINITIONS[selectedItem.typeKey] || null : null),
    [selectedItem],
  )

  const selectedActionButtonConfig = useMemo<ResolvedActivityFeedActionButtonConfig>(
    () => {
      const baseConfig = selectedInlineSlotDefinition?.actionButton || {
        label: "Open Related Page",
      }
      const resolvedLabel =
        typeof baseConfig.label === "function"
          ? selectedItem
            ? baseConfig.label(selectedItem)
            : "Open Related Page"
          : baseConfig.label

      return {
        ...baseConfig,
        label: resolvedLabel,
      }
    },
    [selectedInlineSlotDefinition, selectedItem],
  )

  const selectedInlineComponent = useMemo(() => {
    if (!selectedItem) return null

    const inlineComponent = selectedInlineSlotDefinition?.renderInlineComponent?.(selectedItem)
    if (inlineComponent) {
      return inlineComponent
    }

    if (isCompactViewport) return null
    return selectedInlineSlotDefinition?.renderDesktopInlineComponent?.(selectedItem) || null
  }, [selectedInlineSlotDefinition, selectedItem, isCompactViewport])

  const selectedInlineSectionLabel = useMemo(() => {
    const sectionLabel = selectedInlineSlotDefinition?.sectionLabel

    if (typeof sectionLabel === "function") {
      return selectedItem ? sectionLabel(selectedItem) : "Complete This Here"
    }

    return sectionLabel || "Complete This Here"
  }, [selectedInlineSlotDefinition, selectedItem])

  const selectedItemVariant = useMemo<"default" | "completed" | "ignored">(() => {
    if (!selectedItem) return "default"
    if (selectedItem.status === "completed" || selectedItem.status === "resolved") {
      return "completed"
    }
    if (selectedItem.isDismissed) {
      return "ignored"
    }
    return "default"
  }, [selectedItem])

  const emitRefetch = () => {
    window.dispatchEvent(new Event(ACTIVITY_FEED_REFETCH_EVENT))
  }

  const loadData = useCallback(async () => {
    if (!visible || !user?.userId) {
      return
    }

    setLoading(true)
    try {
      const summaryQuery = buildQueryString({ userId: user.userId })
      const feedQuery = buildQueryString({
        userId: user.userId,
        status: "active",
        limit: 100,
      })

      const [summaryResponse, feedResponse] = await Promise.all([
        swimminglyApi.get(gen(`/api/activity-feed/summary?${summaryQuery}`)),
        swimminglyApi.get(gen(`/api/activity-feed?${feedQuery}`)),
      ])

      const summaryPayload: any = (summaryResponse as any)?.data ?? summaryResponse ?? {}
      const feedPayload: any = (feedResponse as any)?.data ?? feedResponse ?? {}

      setSummary(
        summaryPayload.summary || {
          unreadCount: summaryPayload.unreadCount || 0,
        },
      )
      setItems(Array.isArray(feedPayload.items) ? feedPayload.items : [])
    } catch (err: any) {
      console.error("ActivityFeedDrawer load error", err)
      message.error(err?.message || "Unable to load activity feed")
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [visible, user?.userId])

  const loadTabData = useCallback(
    async (tab: "completed" | "dismissed" | "archived") => {
      if (!user?.userId) return

      setTabLoading(true)
      try {
        const feedQuery = buildQueryString({ userId: user.userId, view: tab, limit: 100 })
        const response: any = await swimminglyApi.get(gen(`/api/activity-feed?${feedQuery}`))
        const payload: any = (response as any)?.data ?? response ?? {}
        const tabItems = Array.isArray(payload.items) ? payload.items : []

        if (tab === "completed") {
          setCompletedItems(tabItems)
        } else if (tab === "dismissed") {
          setDismissedItems(tabItems)
        } else {
          setArchivedItems(tabItems)
        }
      } catch (err: any) {
        console.error(`ActivityFeedDrawer ${tab} tab load error`, err)
        message.error(err?.message || `Unable to load ${tab} items`)
      } finally {
        setTabLoading(false)
      }
    },
    [user?.userId],
  )

  const handleTabChange = (key: string) => {
    const tab = key as "main" | "completed" | "dismissed" | "archived"
    setActiveTab(tab)
    // Clear the restore notice whenever the user navigates away from the Ignored tab
    if (tab !== "dismissed") {
      setIgnoredTabNotice(null)
    }
    if (tab !== "main" && !loadedTabs.has(tab)) {
      setLoadedTabs((prev) => { const next = new Set(prev); next.add(tab); return next })
      loadTabData(tab as "completed" | "dismissed" | "archived")
    }
  }

  useEffect(() => {
    loadData()
  }, [loadData])

  useEffect(() => {
    if (
      (activeTab === "completed" || activeTab === "dismissed" || activeTab === "archived") &&
      !loadedTabs.has(activeTab)
    ) {
      setLoadedTabs((prev) => {
        const next = new Set(prev)
        next.add(activeTab)
        return next
      })
      loadTabData(activeTab as "completed" | "dismissed" | "archived")
    }
  }, [activeTab, loadedTabs, loadTabData])

  useEffect(() => {
    const handleResize = () => {
      setIsCompactViewport(window.innerWidth < 768)
    }

    window.addEventListener("resize", handleResize)
    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [])

  useEffect(() => {
    const handleRefetch = () => {
      ;(async () => {
        await loadData()
        // Reset secondary tab caches so the next visit forces a fresh load
        setLoadedTabs(new Set(["main"]))
        setCompletedItems([])
        setDismissedItems([])
        setArchivedItems([])

        if (
          detailVisible &&
          selectedItem &&
          selectedItem.typeKey === "upload_team_logo_missing"
        ) {
          const response: any = await swimminglyApi.get(
            gen(
              `/api/activity-feed/${encodeURIComponent(selectedItem.itemId)}?${buildQueryString({
                userId: user?.userId,
              })}`,
            ),
          )
          const payload: any = response?.data ?? response ?? {}
          const refreshedItem: ActivityFeedItem | null = payload?.item || null

          if (
            refreshedItem &&
            (refreshedItem.status === "completed" || refreshedItem.status === "resolved")
          ) {
            setDetailVisible(false)
            setDetailItem(null)
            setSelectedItemId(null)
          }
        }
      })()
    }

    window.addEventListener(ACTIVITY_FEED_REFETCH_EVENT, handleRefetch)
    return () => {
      window.removeEventListener(ACTIVITY_FEED_REFETCH_EVENT, handleRefetch)
    }
  }, [loadData])

  const runMutation = async (itemId: string, callback: () => Promise<void>) => {
    setMutatingIds((current) => [...current, itemId])
    try {
      await callback()
      emitRefetch()
      await loadData()
      if (activeTab === "completed" || activeTab === "dismissed" || activeTab === "archived") {
        await loadTabData(activeTab)
      }
    } catch (err: any) {
      console.error("ActivityFeedDrawer mutation error", err)
      message.error(err?.message || "Unable to update activity feed item")
    } finally {
      setMutatingIds((current) => current.filter((id) => id !== itemId))
    }
  }

  const loadItemDetail = async (itemId: string) => {
    try {
      const response: any = await swimminglyApi.get(
        gen(`/api/activity-feed/${encodeURIComponent(itemId)}?${buildQueryString({ userId: user?.userId })}`),
      )
      const payload: any = response?.data ?? response ?? {}
      if (payload?.item) {
        setDetailItem(payload.item)
      }
    } catch (err) {
      console.error("ActivityFeedDrawer detail load error", err)
    }
  }

  const navigateFromAction = (action?: ActivityFeedAction | null, item?: ActivityFeedItem | null) => {
    if (action?.kind === "external_link" && action?.route) {
      window.open(action.route, "_blank", "noopener,noreferrer")
      return
    }

    const overriddenGuardianRoute = item ? buildGuardianParentMeetScheduleRoute(item) : null
    const route = overriddenGuardianRoute || action?.route

    if (!route) return

    if (route === "/app/billing") {
      history.push("/app/billing?tab=websites")
    } else if (route === "/app/club/admin") {
      const query = new URLSearchParams()
      if (action?.tabKey) {
        query.set("tab", action.tabKey)
      }
      if (action?.clubId) {
        query.set("clubId", String(action.clubId))
      }
      const queryString = query.toString()
      history.push(queryString ? `${route}?${queryString}` : route)
    } else {
      history.push(route)
    }

    setDetailVisible(false)
    setVisible(false)
  }

  const handleMarkRead = async (item: ActivityFeedItem) => {
    await runMutation(item.itemId, async () => {
      await swimminglyApi
        .post(gen(`/api/activity-feed/${encodeURIComponent(item.itemId)}/read`))
        .body({ userId: user?.userId })
    })
  }

  const handleMarkUnread = async (item: ActivityFeedItem) => {
    await runMutation(item.itemId, async () => {
      await swimminglyApi
        .post(gen(`/api/activity-feed/${encodeURIComponent(item.itemId)}/unread`))
        .body({ userId: user?.userId })
    })
  }

  const handleArchive = async (item: ActivityFeedItem) => {
    await runMutation(item.itemId, async () => {
      await swimminglyApi
        .post(gen(`/api/activity-feed/${encodeURIComponent(item.itemId)}/archive`))
        .body({ userId: user?.userId })
    })

    if (selectedItemId === item.itemId) {
      setDetailVisible(false)
      setSelectedItemId(null)
    }

    if (loadedTabs.has("archived")) {
      await loadTabData("archived")
    }
  }

  const handleDismiss = async (item: ActivityFeedItem) => {
    await runMutation(item.itemId, async () => {
      await swimminglyApi
        .post(gen(`/api/activity-feed/${encodeURIComponent(item.itemId)}/dismiss`))
        .body({ userId: user?.userId })
    })

    if (selectedItemId === item.itemId) {
      setDetailVisible(false)
      setDetailItem(null)
      setSelectedItemId(null)
    }

    if (loadedTabs.has("dismissed")) {
      await loadTabData("dismissed")
    }
  }

  const handleUnignore = async (item: ActivityFeedItem) => {
    await runMutation(item.itemId, async () => {
      await swimminglyApi
        .post(gen(`/api/activity-feed/${encodeURIComponent(item.itemId)}/unignore`))
        .body({ userId: user?.userId })
    })

    setIgnoredTabNotice("Notification restored to your Activity feed.")

    if (selectedItemId === item.itemId) {
      setDetailVisible(false)
      setDetailItem(null)
      setSelectedItemId(null)
    }
  }

  const handleAction = async (item: ActivityFeedItem) => {
    await runMutation(item.itemId, async () => {
      const response: any = await swimminglyApi
        .post(gen(`/api/activity-feed/${encodeURIComponent(item.itemId)}/action`))
        .body({ userId: user?.userId })

      // version_update acknowledging completes the notification server-side —
      // no navigation; let the feed refresh show the completed state.
      if (item.typeKey === "version_update") {
        return
      }

      const payload: any = response?.data ?? response ?? {}
      navigateFromAction(payload.action || item.primaryAction, item)
    })

    // Close the detail modal but keep the drawer open so the user sees the
    // updated (now-completed) feed.
    if (item.typeKey === "version_update") {
      setDetailVisible(false)
      setDetailItem(null)
    }
  }

  const openItemDetails = async (item: ActivityFeedItem) => {
    if (activeTab === "dismissed") {
      return
    }

    setSelectedItemId(item.itemId)
    setDetailItem(item)
    setDetailVisible(true)

    loadItemDetail(item.itemId)

    const isActionOriented =
      item.recipientIntent === "action_required" || item.recipientIntent === "oversight_required"
    if (item.isUnread && item.capabilities.canRead && !isActionOriented) {
      await handleMarkRead(item)
    }
  }

  const renderMeetDeclarationsDetail = (item: ActivityFeedItem) => {
    const itemContext = item.context || {}
    const liveSummary = itemContext.liveSummary || {}
    const recipientSnapshot = itemContext.recipientSnapshot || {}
    const swimmers: MeetDeclarationsSwimmer[] = Array.isArray(itemContext.swimmers)
      ? itemContext.swimmers
      : []
    const totalRosterSwimmerCount = Number(
      liveSummary.totalRosterSwimmerCount || itemContext.totalRosterSwimmerCount || 0,
    )
    const attendingCount = Number(liveSummary.attendingCount || itemContext.attendingCount || 0)
    const notAttendingCount = Number(
      liveSummary.notAttendingCount || itemContext.notAttendingCount || 0,
    )
    const pendingCount = Number(
      liveSummary.pendingCount ??
        itemContext.pendingCount ??
        Math.max(totalRosterSwimmerCount - attendingCount - notAttendingCount, 0),
    )
    const respondedCount = Math.max(attendingCount + notAttendingCount, 0)
    const responsePercent =
      totalRosterSwimmerCount > 0
        ? Math.min(100, Math.round((respondedCount / totalRosterSwimmerCount) * 100))
        : 0
    const statTiles = [
      {
        label: "Pending",
        value: pendingCount,
        accent: "#faad14",
        background: "#fff7e6",
      },
      {
        label: "Attending",
        value: attendingCount,
        accent: "#52c41a",
        background: "#f6ffed",
      },
      {
        label: "Not attending",
        value: notAttendingCount,
        accent: "#ff4d4f",
        background: "#fff1f0",
      },
      {
        label: "Total roster",
        value: totalRosterSwimmerCount,
        accent: "#1677ff",
        background: "#f0f5ff",
      },
    ]

    if (item.typeKey !== "meet_declarations_open") {
      return null
    }

    return (
      <Space orientation="vertical" size={12} style={{ width: "100%" }}>
        {recipientSnapshot.hasAdminContext ? (
          <Card
            size="small"
            title="Admin Oversight"
            styles={{
              body: {
                padding: 20,
                border: "1px solid #d7ddff",
                background: "linear-gradient(180deg, #fcfdff 0%, #f5f8ff 100%)",
              },
            }}
          >
            <Space orientation="vertical" size={16} style={{ width: "100%" }}>
              <div>
                <Text
                  type="secondary"
                  style={{
                    display: "block",
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    marginBottom: 6,
                  }}
                >
                  Meet Declarations Responses
                </Text>
                <Space
                  wrap
                  align="start"
                  size={[12, 8]}
                  style={{ width: "100%", justifyContent: "space-between" }}
                >
                  <Space orientation="vertical" size={2}>
                    <Text strong style={{ fontSize: 18, lineHeight: 1.3 }}>
                      {item.context?.meetName || "Meet declarations"}
                    </Text>
                    <Text type="secondary">
                      {item.context?.clubNameLong || item.context?.clubNameShort || "Club"}
                    </Text>
                  </Space>
                  {item.context?.meetDeclarationsCloseDate ? (
                    <Tag color="blue" style={{ marginInlineEnd: 0 }}>
                      Meet Declarations Deadline {formatTimestamp(item.context.meetDeclarationsCloseDate)}
                    </Tag>
                  ) : null}
                </Space>
              </div>

              <div
                style={{
                  background: "#ffffff",
                  border: "1px solid #e5e9f5",
                  borderRadius: 12,
                  padding: 16,
                }}
              >
                <Space orientation="vertical" size={8} style={{ width: "100%" }}>
                  <Text strong style={{ fontSize: 22, lineHeight: 1.2, color: "#101242" }}>
                    {respondedCount} of {totalRosterSwimmerCount} swimmers responded
                  </Text>
                  <Text type="secondary">
                    {pendingCount > 0
                      ? `${pendingCount} declaration${pendingCount === 1 ? " is" : "s are"} still pending.`
                      : "All swimmers have submitted a declaration response."}
                  </Text>
                  <Progress
                    percent={responsePercent}
                    showInfo={false}
                    strokeColor="#1677ff"
                    railColor="#e8ecf7"
                  />
                </Space>
              </div>

              <Row gutter={[10, 10]}>
                {statTiles.map((tile) => (
                  <Col xs={12} sm={12} md={6} key={tile.label}>
                    <div
                      style={{
                        background: tile.background,
                        border: `1px solid ${tile.accent}22`,
                        borderRadius: 10,
                        padding: 12,
                        minHeight: 84,
                      }}
                    >
                      <Text
                        type="secondary"
                        style={{ display: "block", fontSize: 11, marginBottom: 6 }}
                      >
                        {tile.label}
                      </Text>
                      <Text strong style={{ fontSize: 24, lineHeight: 1, color: tile.accent }}>
                        {tile.value}
                      </Text>
                    </div>
                  </Col>
                ))}
              </Row>
            </Space>
          </Card>
        ) : null}

        {recipientSnapshot.hasGuardianContext ? (
          <Card size="small" title="Your Swimmers">
            <Space orientation="vertical" size={8} style={{ width: "100%" }}>
              {swimmers.length === 0 ? (
                <Text type="secondary">No swimmer declarations are currently tied to your account for this meet.</Text>
              ) : (
                swimmers.map((swimmer) => (
                  <Card key={swimmer.swimmerId} size="small" styles={{ body: { padding: 12 } }}>
                    <Space orientation="vertical" size={4} style={{ width: "100%" }}>
                      <Space wrap>
                        <Text strong>{[swimmer.firstName, swimmer.lastName].filter(Boolean).join(" ")}</Text>
                        <Tag color={swimmer.status === "attending" ? "green" : swimmer.status === "notAttending" ? "red" : "gold"}>
                          {titleize(swimmer.status)}
                        </Tag>
                      </Space>
                      {swimmer.lastDeclarationActorName ? (
                        <Text type="secondary">Last updated by {swimmer.lastDeclarationActorName}</Text>
                      ) : null}
                    </Space>
                  </Card>
                ))
              )}
            </Space>
          </Card>
        ) : null}
      </Space>
    )
  }

  function renderItemCard(
    item: ActivityFeedItem,
    opts: { showReadToggle?: boolean; variant?: "default" | "completed" | "ignored" | "archived" } = {},
  ) {
    const isMutating = mutatingIds.includes(item.itemId)
    const { showReadToggle = true, variant = "default" } = opts
    const isCompletedVariant = variant === "completed"
    const isIgnoredVariant = variant === "ignored"
    const isArchivedVariant = variant === "archived"
    const isUnreadActivityCard =
      variant === "default" &&
      item.isUnread
    const cardBody = getCardBody(item, variant)
    const completionOutcome = getVirtualMeetInvitationCompletionOutcome(item)
    const cardHeaderMeta = renderJourneyStepTag(item, variant)
    const cardTimestamp =
      isIgnoredVariant && item.dismissedAt
        ? `Ignored ${formatTimestamp(item.dismissedAt)}`
        : isCompletedVariant && item.completedAt
          ? `Completed ${formatTimestamp(item.completedAt)}`
          : isArchivedVariant && item.archivedAt
            ? `Archived ${formatTimestamp(item.archivedAt)}`
            : formatTimestamp(item.triggeredAt)
    const cardStyles = {
      header: {
        background: "rgba(255, 255, 255, 0.78)",
        borderBottom: "1px solid rgba(15, 23, 42, 0.06)",
        paddingTop: 8,
        paddingBottom: 8,
      },
      body: {
        boxShadow: "0 6px 18px rgba(15, 23, 42, 0.045)",
        borderLeft: isCompletedVariant
          ? completionOutcome === "declined"
            ? "3px solid #ff4d4f"
            : "3px solid #52c41a"
          : isIgnoredVariant || isArchivedVariant
            ? "3px solid #8c8c8c"
            : isUnreadActivityCard
              ? "3px solid #1677ff"
              : "3px solid rgba(148, 163, 184, 0.24)",
        background: isCompletedVariant
          ? completionOutcome === "declined"
            ? "linear-gradient(180deg, #fff6f5 0%, #fff1f0 100%)"
            : "linear-gradient(180deg, #ffffff 0%, #f8fff7 100%)"
          : isIgnoredVariant || isArchivedVariant
            ? "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)"
            : isUnreadActivityCard
              ? "linear-gradient(180deg, #ffffff 0%, #f5f9ff 100%)"
              : "linear-gradient(180deg, #ffffff 0%, #fbfcfe 100%)",
      },
    }

    const card = (
      <Card
        key={item.itemId}
        hoverable={!isIgnoredVariant && !isArchivedVariant}
        size="small"
        style={{ borderRadius: 14, overflow: "hidden", borderColor: "rgba(15, 23, 42, 0.08)" }}
        styles={cardStyles}
        onClick={isIgnoredVariant || isArchivedVariant ? undefined : () => openItemDetails(item)}
        title={
          <Space size={8} wrap style={{ minWidth: 0 }}>
            {renderCardTitleContent(item, variant, isUnreadActivityCard)}
            {isCompletedVariant ? renderCompletionOutcomeTag(item) : null}
          </Space>
        }
        extra={
          <Space orientation="vertical" size={2} align="end" style={{ minWidth: 118 }}>
            <div style={{ minHeight: 22 }}>{cardHeaderMeta}</div>
            <Text type="secondary" style={{ fontSize: 11, lineHeight: 1.2, opacity: 0.72 }}>
              {isUnreadActivityCard ? (
                <span
                  style={{
                    display: "inline-block",
                    width: 5,
                    height: 5,
                    borderRadius: "50%",
                    background: "#1677ff",
                    marginRight: 5,
                    verticalAlign: "middle",
                  }}
                />
              ) : null}
              {cardTimestamp}
            </Text>
          </Space>
        }
      >
        {cardBody ? (
          item.typeKey === "version_update" ? (
            <div
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: cardBody }}
              style={{
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                marginBottom: showReadToggle ? 12 : 0,
                fontSize: 14,
                color: "rgba(0,0,0,0.88)",
                lineHeight: 1.5,
              }}
            />
          ) : (
            <Paragraph ellipsis={{ rows: 2 }} style={{ marginBottom: showReadToggle ? 12 : 0 }}>
              {cardBody}
            </Paragraph>
          )
        ) : null}

        {isCompletedVariant ? (
          <Text type="secondary">
            {getCompletedByText(item)}
            {item.completedAt ? ` on ${formatTimestamp(item.completedAt)}` : ""}
          </Text>
        ) : null}

        {isIgnoredVariant ? (
          <Space style={{ width: "100%", justifyContent: "space-between" }}>
            <Text type="secondary">
              Ignored by {user?.name || "you"}{item.dismissedAt ? ` on ${formatTimestamp(item.dismissedAt)}` : ""}
            </Text>
            <span onClick={(event) => event.stopPropagation()}>
              <Tooltip title="Restore to Activity Feed">
                <Button
                  type="text"
                  icon={<FontAwesomeIcon icon={faRotateLeft} />}
                  loading={isMutating}
                  onClick={() => handleUnignore(item)}
                />
              </Tooltip>
            </span>
          </Space>
        ) : null}

        {isArchivedVariant ? (
          <Text type="secondary">
            Archived{item.archivedAt ? ` on ${formatTimestamp(item.archivedAt)}` : ""}
          </Text>
        ) : null}

        {showReadToggle ? (
          <Space wrap onClick={(event) => event.stopPropagation()}>
            {item.isUnread ? (
              <Tooltip title="Mark Read">
                <Button
                  size="small"
                  type="text"
                  loading={isMutating}
                  icon={<FontAwesomeIcon icon={faEnvelope} />}
                  aria-label="Mark Read"
                  onClick={() => handleMarkRead(item)}
                />
              </Tooltip>
            ) : (
              <Tooltip title="Mark Unread">
                <Button
                  size="small"
                  type="text"
                  loading={isMutating}
                  icon={<FontAwesomeIcon icon={faEnvelopeOpen} />}
                  aria-label="Mark Unread"
                  onClick={() => handleMarkUnread(item)}
                />
              </Tooltip>
            )}
            {item.capabilities.canArchive ? (
              <Tooltip title="Archive">
                <Button
                  size="small"
                  type="text"
                  loading={isMutating}
                  icon={<FontAwesomeIcon icon={faBoxArchive} />}
                  aria-label="Archive"
                  onClick={() => handleArchive(item)}
                />
              </Tooltip>
            ) : null}
          </Space>
        ) : null}
      </Card>
    )

    return card
  }

  function renderMainTabContent() {
    if (loading) {
      return <Skeleton active paragraph={{ rows: 8 }} />
    }

    const needsAttentionItems = items.filter(
      (item) =>
        item.recipientIntent === "action_required" || item.recipientIntent === "oversight_required",
    )
    const otherItems = items.filter(
      (item) =>
        item.recipientIntent === "informational" || item.recipientIntent === "awareness_only",
    )

    if (needsAttentionItems.length === 0 && otherItems.length === 0) {
      return <Empty description="No activity feed items right now." />
    }

    return (
      <Space orientation="vertical" size={10} style={{ width: "100%" }}>
        {needsAttentionItems.length > 0 ? (
          <>
            <Divider titlePlacement="left" style={{ margin: "0 0 4px" }}>
              <Text strong style={{ fontSize: 12 }}>
                Needs Attention
              </Text>
            </Divider>
            {needsAttentionItems.map((item) => renderItemCard(item, { showReadToggle: false }))}
          </>
        ) : null}

        {otherItems.length > 0 ? (
          <>
            {needsAttentionItems.length > 0 ? (
              <Divider titlePlacement="left" style={{ margin: "8px 0 4px" }}>
                <Text strong style={{ fontSize: 12 }}>
                  Updates
                </Text>
              </Divider>
            ) : null}
            {otherItems.map((item) => renderItemCard(item, { showReadToggle: true }))}
          </>
        ) : null}
      </Space>
    )
  }

  function renderSecondaryTabContent(tabItems: ActivityFeedItem[], emptyText: string) {
    if (tabLoading) {
      return <Skeleton active paragraph={{ rows: 5 }} />
    }

    const notice =
      activeTab === "dismissed" && ignoredTabNotice ? (
        <Alert
          type="success"
          showIcon
          title={ignoredTabNotice}
          closable={{ onClose: () => setIgnoredTabNotice(null) }}
        />
      ) : null

    if (tabItems.length === 0) {
      return (
        <Space orientation="vertical" size={12} style={{ width: "100%" }}>
          {notice}
          <Empty description={emptyText} />
        </Space>
      )
    }

    return (
      <Space orientation="vertical" size={10} style={{ width: "100%" }}>
        {notice}
        {tabItems.map((item) =>
          renderItemCard(item, {
            showReadToggle: false,
            variant: activeTab === "completed" ? "completed" : activeTab === "archived" ? "archived" : "ignored",
          }),
        )}
      </Space>
    )
  }

  function renderDetailModalFooter() {
    if (!selectedItem) return null

    if (isCloseOnlyDetailType(selectedItem)) {
      return null
    }

    const isActionOriented =
      selectedItem.recipientIntent === "action_required" ||
      selectedItem.recipientIntent === "oversight_required"

    return (
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 12,
          width: "100%",
          flexWrap: "wrap",
        }}
      >
        <Space wrap>
          {selectedItem.capabilities.canDismiss ? (
            <Tooltip title="Ignore notifications & remove from Activity feed">
              <Button
                danger
                icon={<StopOutlined />}
                title="Ignore"
                aria-label="Ignore"
                style={{ border: "none", boxShadow: "none" }}
                onClick={() => handleDismiss(selectedItem)}
                loading={mutatingIds.includes(selectedItem.itemId)}
              >
                Ignore
              </Button>
            </Tooltip>
          ) : null}
          {selectedItem.capabilities.canTakeAction ? (
            <Button type="primary" onClick={() => handleAction(selectedItem)}>
              <Space size={8}>
                {selectedActionButtonConfig.icon ? (
                  <FontAwesomeIcon icon={selectedActionButtonConfig.icon} />
                ) : null}
                <span>{selectedActionButtonConfig.label}</span>
              </Space>
            </Button>
          ) : null}
        </Space>
      </div>
    )
  }

  const unreadCount = summary?.unreadCount || 0
  const selectedCompletionOutcome = selectedItem
    ? getVirtualMeetInvitationCompletionOutcome(selectedItem)
    : null
  const isDeclinedCompletedInvitation = selectedCompletionOutcome === "declined"

  return (
    <>
      <Drawer
        open={visible}
        onClose={() => setVisible(false)}
        placement="right"
        size={520}
        style={{
          borderTopLeftRadius: 20,
          borderBottomLeftRadius: 20,
          overflow: "hidden",
        }}
        styles={{
          body: {
            background: "linear-gradient(180deg, #f5f7fb 0%, #edf2f8 100%)",
            padding: 14,
          },
          header: {
            background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
            borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
          },
        }}
        title={
          <Space>
            <span>Activity Feed</span>
            <Badge count={unreadCount} />
          </Space>
        }
      >
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          size="small"
          items={[
            {
              key: "main",
              label: (
                <Badge count={unreadCount} size="small" offset={[6, -2]}>
                  <span style={{ paddingRight: 6, fontWeight: 700, color: "#1f1f1f" }}>
                    Activity
                  </span>
                </Badge>
              ),
              children: renderMainTabContent(),
            },
            {
              key: "completed",
              label: <span style={{ opacity: 0.75, fontWeight: 500 }}>Completed</span>,
              children: renderSecondaryTabContent(completedItems, "No completed items yet."),
            },
            {
              key: "dismissed",
              label: <span style={{ opacity: 0.75, fontWeight: 500 }}>Ignored</span>,
              children: renderSecondaryTabContent(dismissedItems, "No ignored items yet."),
            },
            {
              key: "archived",
              label: <span style={{ opacity: 0.75, fontWeight: 500 }}>Archived</span>,
              children: renderSecondaryTabContent(archivedItems, "No archived items yet."),
            },
          ]}
        />
      </Drawer>

      <Modal
        open={detailVisible}
        title={
          selectedItem
            ? renderActivityFeedDetailTitleMeta(
                selectedItem,
                selectedItemVariant,
                isCompactViewport,
              )
            : "Activity Feed Item"
        }
        width={isCompactViewport ? undefined : 980}
        style={isCompactViewport ? undefined : { top: 28 }}
        styles={{
          header: selectedItem
            ? {
                paddingRight: isCompactViewport ? 32 : 40,
              }
            : undefined,
          body: isCompactViewport
            ? {
                background: "linear-gradient(180deg, #f8fafd 0%, #f1f5f9 100%)",
              }
            : {
                background: "linear-gradient(180deg, #f8fafd 0%, #f1f5f9 100%)",
                maxHeight: "calc(100vh - 220px)",
                overflowY: "auto",
                paddingBottom: 20,
              },
        }}
        onCancel={() => {
          setDetailVisible(false)
          setDetailItem(null)
        }}
        footer={renderDetailModalFooter()}
      >
        {selectedItem ? (
          <Space orientation="vertical" size={16} style={{ width: "100%" }}>
            {selectedItem.status === "completed" || selectedItem.status === "resolved" ? (
              <>
                <Card
                  size="small"
                  style={{ borderRadius: 14, borderColor: "rgba(15, 23, 42, 0.08)" }}
                  styles={{
                    body: {
                      background: isDeclinedCompletedInvitation
                        ? "linear-gradient(180deg, #fff6f5 0%, #fff1f0 100%)"
                        : "linear-gradient(180deg, #ffffff 0%, #f8fff7 100%)",
                      border: isDeclinedCompletedInvitation
                        ? "1px solid #ffccc7"
                        : "1px solid #b7eb8f",
                      padding: 18,
                    },
                  }}
                >
                  <Space orientation="vertical" size={6} style={{ width: "100%" }}>
                    <div>
                      {renderModalSectionLabel("Completion")}
                      <Space wrap>
                        {renderJourneyStepTag(selectedItem, "completed")}
                        {renderCompletionOutcomeTag(selectedItem)}
                      </Space>
                    </div>
                    <Space size={8} align="start">
                      <CheckCircleTwoTone twoToneColor={isDeclinedCompletedInvitation ? "#ff4d4f" : "#52c41a"} />
                      <Space orientation="vertical" size={0}>
                        <Text strong>
                          {selectedCompletionOutcome === "accepted"
                            ? "Accepted"
                            : selectedCompletionOutcome === "declined"
                              ? "Declined"
                              : "Completed"}
                        </Text>
                        {selectedItem.triggeredAt ? (
                          <Text type="secondary">Sent {formatTimestamp(selectedItem.triggeredAt)}</Text>
                        ) : null}
                        <Text type="secondary">
                          {getCompletedByText(selectedItem)}
                          {selectedItem.completedAt
                            ? ` on ${formatTimestamp(selectedItem.completedAt)}`
                            : "."}
                        </Text>
                      </Space>
                    </Space>
                    {(selectedItem.completionBody || selectedItem.body) ? (
                      selectedItem.typeKey === "version_update" ? (
                        <div
                          // eslint-disable-next-line react/no-danger
                          dangerouslySetInnerHTML={{ __html: selectedItem.completionBody || selectedItem.body || "" }}
                          style={{ lineHeight: 1.6 }}
                        />
                      ) : (
                        <Paragraph style={{ marginBottom: 0 }}>
                          {selectedItem.completionBody || selectedItem.body}
                        </Paragraph>
                      )
                    ) : null}
                  </Space>
                </Card>
              </>
            ) : (
              <>
                {selectedItem.typeKey !== "meet_declarations_open" ? (
                  <Card
                    size="small"
                    style={{ borderRadius: 14, borderColor: "rgba(15, 23, 42, 0.08)" }}
                    styles={{
                      body: {
                        background: "linear-gradient(180deg, #ffffff 0%, #fbfcfe 100%)",
                        padding: 18,
                      },
                    }}
                  >
                    <Space orientation="vertical" size={10} style={{ width: "100%" }}>
                      <div>
                        {renderModalSectionLabel("Details")}
                        <Space wrap>
                          {renderJourneyStepTag(
                            selectedItem,
                            selectedItem.isDismissed ? "ignored" : "default",
                          )}
                          {selectedItem.typeKey === "version_update" && selectedItem.context?.releaseDate ? (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              Released {new Date(selectedItem.context.releaseDate as string).toLocaleDateString([], { year: "numeric", month: "numeric", day: "numeric" })}
                            </Text>
                          ) : null}
                        </Space>
                      </div>

                      {selectedItem.typeKey === "version_update" && selectedItem.body ? (
                        <div
                          // eslint-disable-next-line react/no-danger
                          dangerouslySetInnerHTML={{ __html: selectedItem.body }}
                          style={{ lineHeight: 1.6 }}
                        />
                      ) : null}
                      {selectedItem.typeKey === "version_update" && selectedItem.context?.mediaUrl ? (
                        <img
                          src={selectedItem.context.mediaUrl}
                          alt="App update"
                          style={{ width: "100%", borderRadius: 8 }}
                        />
                      ) : null}
                      {selectedItem.typeKey !== "version_update" && selectedItem.body ? (
                        <Paragraph style={{ marginBottom: 0 }}>{selectedItem.body}</Paragraph>
                      ) : null}
                      {renderInPersonResultsPublishedDetailNote(selectedItem)}
                      {renderVirtualMeetResultsPublishedDetailNote(selectedItem)}
                    </Space>
                  </Card>
                ) : null}
              </>
            )}

            {renderMeetDeclarationsDetail(selectedItem)}

            {selectedInlineComponent ? (
              <Card
                size="small"
                style={{ borderRadius: 14, borderColor: "rgba(15, 23, 42, 0.08)" }}
                styles={{
                  body: {
                    background: "linear-gradient(180deg, #ffffff 0%, #fbfcfe 100%)",
                    padding: 18,
                  },
                }}
              >
                <div>
                  {renderModalSectionLabel(selectedInlineSectionLabel)}
                  {selectedInlineComponent}
                </div>
              </Card>
            ) : null}
          </Space>
        ) : null}
      </Modal>
    </>
  )
}